/* eslint-disable no-continue */
import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Grid, Row, Col, Glyphicon, DropdownButton, MenuItem } from 'react-bootstrap'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import { CSVLink } from 'react-csv/lib'

import './dash.css'
import './Lock/css/lock.css'
import './react-datepicker.css'
import DeliveryItem from './Deliveries/DeliveryItem'
import MyLoader from '../common/components/MyLoader'
import DeliverySkeleton from './Deliveries/DeliverySkeleton'
import RawDeliveryModal from './Deliveries/RawDeliveryModal'
import QToolsModal from './QTools/QToolsModal'
import useSWR from '../hooks/useSWR'
import useSwrPagination from '../hooks/useSwrPagination'
import useQuery from '../hooks/useQuery'
import { queueSave, groupBy, niceDate, updateSwrCache } from '../common/utils'

const deliveryTargets = [
  { label: 'Container', value: 'container' },
  { label: 'Grind', value: 'gate' },
]

/* Filtering based on checkout or not */
const getFromRegexKeys = (re, map) => {
  const result = []
  for (const [key, val] of map.entries()) {
    const res = re.exec(key)
    if (res) result.push(val)
  }
  return result
}

const segmentDeliveries = (deliveries) =>
  groupBy(deliveries, (d) => {
    const hasCheckedOut = !!d.checkoutDate
    return hasCheckedOut + (d.checkoutDate || d.created || '') + d.deliveredToQlocxUnit + d.bookedToQlocxUnit + d.lockName + d.vendorName + d.receiverCompany
  })

const skeletons = [1, 2, 3, 4, 5]
const Deliveries = ({ history, match }) => {
  const [startDate, setStartDate] = useState(moment().subtract(1, 'd'))
  const [pageQty, setPageQty] = useState(1)
  const [endDate, setEndDate] = useState(moment().add(1, 'd'))
  const [text, setText] = useState('')
  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [selectedLocks, setSelectedLocks] = useState([])
  const [selectedCities, setSelectedCities] = useState([])
  const [selectedCarriers, setSelectedCarriers] = useState([])
  const [selectedVendors, setSelectedVendors] = useState([])
  const [selectedCompanies, setSelectedCompanies] = useState([])
  const [selectedDeliveryTargets, setSelectedDeliveryTargets] = useState([])
  const [exportIsLoading, setExportIsLoading] = useState(false)
  const [futureDeliveriesCollapsed, setFutureDeliveriesCollapsed] = useState(false)
  const [qToolsModalData, setQToolsModalData] = useState(null)

  const deliveriesFilter = {
    text,
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
    vendors: selectedVendors,
    targets: selectedDeliveryTargets,
    locks: selectedLocks,
    carriers: selectedCarriers,
    city: selectedCities,
  }

  const { data: admin } = useSWR('/subject/admin/admin')
  const { data: vendors } = useSWR('/subject/admin/vendors')
  const { data: carriers } = useSWR('/subject/admin/carriers')
  const { data: locks } = useSWR('/subject/admin/locks', { isCarrierDoor: false, sort: { custom_name: -1 }, projection: { custom_name: 1 } })
  const { data: csvExport } = useSWR(exportIsLoading && '/subject/admin/deliveries', { ...deliveriesFilter, isExport: true })
  const { data: deliveries, isValidating } = useSwrPagination('/subject/admin/deliveries', pageQty, deliveriesFilter, { revalidateOnFocus: true, revalidateOnMount: true })
  const query = useQuery()

  const csvLink = useRef(null)

  const segmentedDeliveries = useMemo(() => segmentDeliveries(deliveries || []), [deliveries])
  const customers = useMemo(() => Object.values((deliveries || []).reduce((acc, d) => ({ [d.to_customer && d.to_customer._id]: { ...d.to_customer }, ...acc }), {})).sort((a, b) => (`${a.name} + ${a.surname}` > `${b.name} + ${b.surname}` ? 1 : -1)), [deliveries])
  const filteredDeliveries = useMemo(() => {
    if (selectedCustomers.length === 0 && selectedCompanies.length === 0 && selectedCities.length === 0) return segmentedDeliveries

    return segmentedDeliveries.filter((ds) => {
      for (const d of ds) {
        let includeVotes = 0
        if (selectedCustomers.length === 0) includeVotes += 1
        else if (d.to_customer && d.to_customer.name && selectedCustomers.includes(d.to_customer._id)) includeVotes += 1

        if (selectedCompanies.length === 0) includeVotes += 1
        else if (selectedCompanies.includes(d.receiverCompany)) includeVotes += 1

        if (selectedCities.length === 0) includeVotes += 1
        else if (selectedCities.includes(d.lockCity)) includeVotes += 1

        /* since we have 2 frontend filter fields, includeVotes should equal 4 in order to be included in the search */
        if (includeVotes === 3) return true
      }

      return false
    })
  }, [selectedCustomers, selectedCompanies, selectedCities, segmentedDeliveries])
  const receiverCompanies = useMemo(() => [...new Set((deliveries || []).map((d) => d.receiverCompany))], [deliveries])

  const finishedDeliveries = useMemo(() => getFromRegexKeys(/^true/, filteredDeliveries).sort(([a], [b]) => new Date(b.checkoutDate) - new Date(a.checkoutDate)), [filteredDeliveries])
  const unfinishedDeliveries = useMemo(() => getFromRegexKeys(/^false/, filteredDeliveries).sort(([a], [b]) => new Date(b.created) - new Date(a.created)), [filteredDeliveries])

  const lastFetched = useMemo(() => (!isValidating ? niceDate(new Date().toISOString()) : ''), [isValidating])

  useEffect(() => {
    if (csvExport) {
      csvLink.current.link.click()
      setExportIsLoading(false)
    }
  }, [csvLink, csvExport])

  const updateSearch = (e) => {
    queueSave('searchString', e.target.value, 500).then((t) => {
      setPageQty(1)

      const val = t[t.length - 1]
      setText(val)
    })
  }

  const toggleSelect = (setSelected, id) => {
    setPageQty(1)

    setSelected((prev) => {
      const idx = prev.findIndex((p) => p === id)

      if (idx < 0) return prev.concat(id)
      return prev.filter((p) => p !== id)
    })
  }

  const openQtools = (d) => {
    setQToolsModalData(d)
  }

  const openRawDeliveryData = (carrierId, packageid) => {
    history.push(`/deliveries/${packageid}?carrierId=${carrierId}`)
  }

  if (!admin) return <MyLoader />

  const rawDeliveryParams = {
    carrierId: query.get('carrierId'),
    packageId: match.params.packageId,
  }

  return (
    <div>
      <MyLoader />

      <div>
        <Grid fluid style={{ padding: 0 }}>
          <Row style={{ justifyContent: 'space-between', padding: 0 }}>
            <Col xs={12} md={2} lg={1} style={{ textAlign: 'center' }}>
              <DropdownButton className="delivery-filter-container" title={selectedLocks.length > 0 ? `Valda: ${selectedLocks.length} ` : 'Projekt '} id="bg-nested-dropdown">
                <MenuItem style={{ backgroundColor: 'rgb(230, 230, 230)' }} className="dropdown-menu-item" onSelect={() => setSelectedLocks([])}>
                  <div>Återställ filter </div>
                </MenuItem>
                <div style={{ maxHeight: '410px', overflowY: 'scroll' }}>
                  {(locks || []).map((lock) => (
                    <MenuItem key={lock._id} eventKey={lock._id} onSelect={() => toggleSelect(setSelectedLocks, lock._id)} className="dropdown-menu-item">
                      <Glyphicon glyph={`glyphicon glyphicon-${selectedLocks.includes(lock._id) ? 'check' : 'unchecked'}`} style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
                      {lock.custom_name ? <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{lock.custom_name}</div> : <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{lock.address.street}</div>}
                    </MenuItem>
                  ))}
                </div>
              </DropdownButton>
            </Col>
            <Col xs={12} md={2} lg={1} style={{ textAlign: 'center' }}>
              <DropdownButton className="delivery-filter-container" title={selectedCities.length > 0 ? `Valda: ${selectedCities.length} ` : 'Ort '} id="bg-nested-dropdown">
                <MenuItem style={{ backgroundColor: 'rgb(230, 230, 230)' }} className="dropdown-menu-item" onSelect={() => setSelectedCities([])}>
                  <div>Återställ filter </div>
                </MenuItem>
                <div style={{ maxHeight: '410px', overflowY: 'scroll' }}>
                  {[...new Set(locks?.map((lock) => lock.city))].map((city) => {
                    const lockObj = locks.find((lock) => lock.city === city)
                    return (
                      <MenuItem key={lockObj._id} eventKey={city} onSelect={() => toggleSelect(setSelectedCities, lockObj.city)} className="dropdown-menu-item">
                        <Glyphicon glyph={`glyphicon glyphicon-${selectedCities.includes(lockObj.city) ? 'check' : 'unchecked'}`} style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
                        <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{city}</div>
                      </MenuItem>
                    )
                  })}
                </div>
              </DropdownButton>
            </Col>
            <Col xs={12} md={2} lg={1} style={{ textAlign: 'center' }}>
              <DropdownButton className="delivery-filter-container" title={selectedCarriers.length > 0 ? `Valda: ${selectedCarriers.length} ` : 'Transportör'} id="bg-nested-dropdown">
                <MenuItem style={{ backgroundColor: 'rgb(230, 230, 230)' }} className="dropdown-menu-item" onSelect={() => setSelectedCarriers([])}>
                  <div>Återställ filter </div>
                </MenuItem>
                <div style={{ maxHeight: '410px', overflowY: 'scroll' }}>
                  {(carriers || []).map((carrier) => (
                    <MenuItem key={carrier._id} eventKey={carrier._id} onSelect={() => toggleSelect(setSelectedCarriers, carrier._id)} className="dropdown-menu-item">
                      <Glyphicon glyph={`glyphicon glyphicon-${selectedCarriers.includes(carrier._id) ? 'check' : 'unchecked'}`} style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
                      <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{carrier.organization}</div>
                    </MenuItem>
                  ))}
                </div>
              </DropdownButton>
            </Col>
            <Col xs={12} md={3} lg={1} style={{ textAlign: 'center' }}>
              <DropdownButton className="delivery-filter-container" title={selectedVendors.length > 0 ? `Valda: ${selectedVendors.length} ` : 'Leverantör '} id="bg-nested-dropdown">
                <MenuItem style={{ backgroundColor: 'rgb(230, 230, 230)' }} className="dropdown-menu-item" onSelect={() => setSelectedVendors([])}>
                  <div>Återställ filter </div>
                </MenuItem>
                <div style={{ maxHeight: '410px', overflowY: 'scroll' }}>
                  {(vendors || []).map((vendor) => (
                    <MenuItem key={vendor._id} onSelect={() => toggleSelect(setSelectedVendors, vendor._id)} className="dropdown-menu-item">
                      <Glyphicon glyph={`glyphicon glyphicon-${selectedVendors.includes(vendor._id) ? 'check' : 'unchecked'}`} style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
                      <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{vendor.name}</div>
                    </MenuItem>
                  ))}
                </div>
              </DropdownButton>
            </Col>

            <Col xs={12} md={2} lg={1} style={{ textAlign: 'center' }}>
              <DropdownButton className="delivery-filter-container" title={selectedCompanies.length > 0 ? `Valda: ${selectedCompanies.length} ` : 'Företag '} id="bg-nested-dropdown">
                <MenuItem style={{ backgroundColor: 'rgb(230, 230, 230)' }} className="dropdown-menu-item" onSelect={() => setSelectedCompanies([])}>
                  <div>Återställ filter </div>
                </MenuItem>
                <div style={{ maxHeight: '410px', overflowY: 'scroll' }}>
                  {receiverCompanies.sort().map((company) => {
                    if (!company || !company.length) return null

                    return (
                      <MenuItem key={company} eventKey={company} onSelect={() => toggleSelect(setSelectedCompanies, company)} className="dropdown-menu-item">
                        <Glyphicon glyph={`glyphicon glyphicon-${selectedCompanies.includes(company) ? 'check' : 'unchecked'}`} style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
                        <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{company}</div>
                      </MenuItem>
                    )
                  })}
                </div>
              </DropdownButton>
            </Col>

            <Col xs={12} md={3} lg={2} style={{ textAlign: 'center', padding: 0 }}>
              <div style={{ borderRadius: 10, height: 42, fontSize: 14, cursor: 'default' }} className="delivery-filter-container">
                <input type="text" onChange={updateSearch} placeholder="Sök" className="Sök" />
              </div>
            </Col>
            <Col xs={12} md={2} lg={1} style={{ padding: 0 }}>
              <DatePicker locale="sv" dateFormat="dd/MM/yyyy" className="delivery-filter-container" selected={startDate.toDate()} onChange={(date) => setStartDate(moment(date))} />
            </Col>

            <Col xs={12} md={2} lg={1} style={{ padding: 0 }}>
              <DatePicker locale="sv" dateFormat="dd/MM/yyyy" className="delivery-filter-container" style={{ cursor: 'text' }} selected={endDate.toDate()} onChange={(date) => setEndDate(moment(date))} />
            </Col>

            <Col xs={12} md={2} lg={1} style={{ textAlign: 'center' }}>
              <DropdownButton className="delivery-filter-container" title={selectedCustomers.length > 0 ? `Valda: ${selectedCustomers.length} ` : 'Person '} id="bg-nested-dropdown">
                <MenuItem style={{ backgroundColor: 'rgb(230, 230, 230)' }} className="dropdown-menu-item" onSelect={() => setSelectedCustomers([])}>
                  <div>Återställ filter </div>
                </MenuItem>
                <div style={{ maxHeight: '410px', overflowY: 'scroll' }}>
                  {(customers || []).map(
                    (customer) =>
                      customer.name && (
                        <MenuItem key={customer._id} className="dropdown-menu-item" eventKey={customer._id} onSelect={() => toggleSelect(setSelectedCustomers, customer._id)}>
                          <Glyphicon glyph={`glyphicon glyphicon-${selectedCustomers.includes(customer._id) ? 'check' : 'unchecked'}`} style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
                          <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>
                            {customer.name} {customer.surname}
                          </div>
                        </MenuItem>
                      )
                  )}
                </div>
              </DropdownButton>
            </Col>

            <Col xs={12} md={1} lg={1} style={{ marginRight: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
              <DropdownButton className="delivery-filter-container" title={selectedDeliveryTargets.length > 0 ? `Valda: ${selectedDeliveryTargets.length} ` : 'Leveranspunkt '} id="bg-nested-dropdown">
                <MenuItem style={{ backgroundColor: 'rgb(230, 230, 230)' }} className="dropdown-menu-item" onSelect={() => setSelectedDeliveryTargets([])}>
                  <div>Återställ filter </div>
                </MenuItem>
                <div style={{ maxHeight: '410px', overflowY: 'scroll' }}>
                  {deliveryTargets.map((target) => (
                    <MenuItem key={target.value} className="dropdown-menu-item" eventKey={target.value} onSelect={() => toggleSelect(setSelectedDeliveryTargets, target.value)}>
                      <Glyphicon glyph={`glyphicon glyphicon-${selectedDeliveryTargets.includes(target.value) ? 'check' : 'unchecked'}`} style={{ color: 'grey', marginRight: '5px', marginLeft: '5px', float: 'left' }} />
                      <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{target.label}</div>
                    </MenuItem>
                  ))}
                </div>
              </DropdownButton>
            </Col>
          </Row>
          <Row style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
            <Col xs={12} md={1} lg={1}>
              <React.Fragment>
                <button disabled={exportIsLoading} onClick={() => setExportIsLoading(true)} className="smallQbutton" style={{ borderRadius: 10, background: exportIsLoading && 'grey' }}>
                  {exportIsLoading ? 'Exporterar...' : 'Exportera'}
                </button>
                <CSVLink data={csvExport || ''} filename={`LeveransExport_${new Date().toISOString()}.csv`} className="hidden" separator={';'} ref={csvLink} target="_blank" />
              </React.Fragment>
            </Col>
          </Row>
        </Grid>

        <br />

        <div style={{ margin: 'auto', marginBottom: '30px', marginTop: '50px' }}></div>

        <Grid className="component-width">
          <Row>
            <Col style={{ cursor: 'pointer' }} onClick={() => setFutureDeliveriesCollapsed(!futureDeliveriesCollapsed)} xs={10}>
              <h3 className="sub-title">
                Kommande ({unfinishedDeliveries.length}) <Glyphicon style={{ fontWeight: 400, fontSize: 16 }} className="sub-title" glyph={`glyphicon glyphicon-menu-${futureDeliveriesCollapsed ? 'right' : 'down'}`} />
              </h3>
            </Col>
            <Col style={{ cursor: 'pointer' }} onClick={() => updateSwrCache('/subject/admin/deliveries?', (p) => p, true)}>
              {`Senast hämtat: ${lastFetched} `}
              <Glyphicon style={{ cursor: 'pointer' }} glyph="glyphicon glyphicon-refresh" />
            </Col>
          </Row>

          <br />
          {!futureDeliveriesCollapsed && (
            <div>
              {unfinishedDeliveries.map((d) => (
                <DeliveryItem openQtools={openQtools} openRawDeliveryData={openRawDeliveryData} shipments={d} key={d[0]._id} />
              ))}
              {isValidating && deliveries.length === 0 && skeletons.map((d) => <DeliverySkeleton key={d} />)}
            </div>
          )}
        </Grid>

        <div style={{ margin: 'auto', marginBottom: '30px', marginTop: '50px' }}></div>

        <Grid className="component-width">
          <Row>
            <Col md={12}>
              <h3 className="sub-title">Avslutade ({finishedDeliveries.length})</h3>
            </Col>
          </Row>

          <br />

          <div>
            {finishedDeliveries.map((d) => (
              <DeliveryItem openQtools={openQtools} openRawDeliveryData={openRawDeliveryData} shipments={d} key={d[0]._id} />
            ))}
            {isValidating && skeletons.map((d) => <DeliverySkeleton key={d} />)}
          </div>

          <Row>
            <Col md={12} style={{ display: 'block', margin: 'auto', textAlign: 'center', cursor: 'pointer', marginTop: '20px' }}>
              <div onClick={() => setPageQty(pageQty + 1)} style={{ fontSize: '20px', marginBottom: '20px', textAlign: 'center' }}>
                Visa mer <Glyphicon glyph="glyphicon glyphicon-menu-down" />
              </div>
            </Col>
          </Row>
        </Grid>
      </div>
      <RawDeliveryModal isModalOpen={!!rawDeliveryParams.packageId} closeModal={() => history.goBack()} rawDeliveryParams={rawDeliveryParams} />
      <QToolsModal isOpen={!!qToolsModalData} deliveries={qToolsModalData} closeModal={() => setQToolsModalData(null)} />
    </div>
  )
}

export default Deliveries
